import { useCallback, useState } from "react";

export interface UseApiRequestOptions {
  /** If true, keep error in hook state for on-screen rendering. */
  persistError?: boolean;
  /** Optional callback for side effects like toast notifications. */
  onError?: (message: string, error: unknown) => void;
}

/**
 * Reusable async wrapper for API calls.
 *
 * - Normalizes loading lifecycle
 * - Normalizes error-message extraction
 * - Supports both screen-level error rendering and side effects (toast)
 */
export const useApiRequest = (options: UseApiRequestOptions = {}) => {
  const { persistError = true, onError } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fn();
        return result;
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : "An unexpected error occurred";

        if (persistError) {
          setError(message);
        }
        onError?.(message, e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [persistError, onError],
  );

  return {
    run,
    isLoading,
    error,
    setError,
  };
};

